<template>
  <v-select
    :items="groups.filter(g => g.value !== 'studentId' || perm !== 'student')"
    v-model="gVal"
    label="Group By"
    dense
  />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import stats from 'stats-lite';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import set from 'lodash/set';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash/cloneDeep';
import groups from '@/lib/groups';

const quickMath = vals => ({
  count: vals.length,
  countNoDupe: vals.length,
  min: Math.min(...vals),
  max: Math.max(...vals),
  mean: stats.mean(vals),
  mode: stats.mode(vals),
  median: stats.median(vals),
  stdDev: stats.stdev(vals),
  passRate: stats.mean(vals.map(Math.round)),
  // histogram: stats.histogram(vals),
});

export default {
  props: {
    query: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      time: {},
      hide: true,
      groups,
      gVal: this.$route.query.g || 'unitId',
      grouped: {},
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('attempts', ['slowPercent']),
    ...mapGetters('attempts', ['find']),
    perm() { return this.user && this.user.perm; },
    group() { return groups.find(g => g.value === (this.$route.query.g || 'unitId')); },
    attempts() {
      if (!this.query.time) return [];
      if (this.slowPercent < 1) return [];
      const gte = new Date(this.query.time.$gte);
      const lte = new Date(this.query.time.$lte);
      const query = {
        ...this.query,
        time: (t) => {
          const time = new Date(t);
          return time <= lte && time >= gte;
        },
        stat: 'complete',
      };
      return this.find({ query }).data;
    },
  },
  methods: {
    computeResult() {
      let flat = [];
      if (!this.group.flatten) flat = this.attempts;
      else {
        flat = this.attempts.reduce((a, att) => [
          ...a,
          ...get(att, this.group.flatten).map((v) => {
            const att2 = { ...att };
            set(att2, this.group.value, v);
            set(att2, this.group.flatten, [v]);
            if (this.group.populated) {
              this.group.populated.forEach((pop) => {
                set(
                  att2,
                  pop.field,
                  pop.id
                    ? [get(att, pop.field).find(p => get(p, pop.id) === v)]
                    : [this.$store.getters[`${pop.field}/get`](v)[pop.label]],
                );
              });
            }
            return att2;
          }),
        ], []);
      }
      const grouped = groupBy(flat, this.group.iteratee || this.group.value);

      // const uniqFlat = (arr, i, j) => uniqBy(flatMap(arr, i), j);
      const results = Object.keys(grouped).map((label) => {
        const atts = grouped[label];
        const group = {
          label,
          push(i, v) {
            const val = Array.isArray(v) ? [...v] : [v];
            if (this[i]) this[i].push(...val);
            else this[i] = val;
          },
        };
        atts.forEach((att) => {
          group.push('times', (new Date(att.time)).getTime());
          group.push('studentIds', att.studentId);
          group.push('supervisorIds', att.supervisorId);
          group.push('locIds', att.locId);
          group.push('unitIds', att.unitId);
          group.push('proIds', att.proIds);
          group.push('stepIds', att.stepIds);
          group.push('students', att.student);
          group.push('students_firstname', att.student_firstname);
          group.push('students_lastname', att.student_lastname);
          group.push('students_username', att.student_username);
          group.push('supervisors', att.supervisor);
          group.push('locations', att.location);
          group.push('units', att.unit);
          group.push('procedures', att.procedures);
          group.push('steps', att.steps);
          group.push('durations', att.duration);
          group.push('grades', att.grade);
          group.push('stepGrades', flatMap(att.grades, 'grade'));
          group.push('teeth', att.teeth);
          group.push('flags', att.flags);
          group.push('patients', `${att.patient} (${moment(att.patientDOB).format('DD/MM/YYYY')})`);
          group.push('noDupe', `${sortBy(att.proIds).join('-')}-${sortBy(att.teeth).join('-')}-${att.patient}`);
        });
        group.weight = quickMath(group.grades);
        group.step = quickMath(group.stepGrades);
        group.time = Math.round(stats.mean(group.times));
        Object.keys(group).forEach((i) => {
          if (Array.isArray(group[i])) group[i] = uniqBy(group[i]);
        });
        group.weight.countNoDupe = group.noDupe.length;
        group.attempts = atts;
        return group;
      });
      this.$emit('update:results', sortBy(results, v => get(v, this.group.label)));
    },
  },
  watch: {
    'query.time': {
      handler(v) {
        if (isEqual(this.time, v)) return;
        this.time = cloneDeep(v);
        this.$store.dispatch('attempts/slowFind', {
          query: {
            time: v,
            stat: 'complete',
            $sort: { time: -1 },
          },
          limit: 15000,
        });
      },
      deep: true,
    },
    attempts(to) {
      if (!to.length) return;
      this.computeResult();
      const refetch = to.filter(att => !att.slowFound).map(att => att._id);
      if (refetch.length) {
        this.$store.dispatch('attempts/slowFind', {
          query: {
            _id: { $in: refetch },
            time: this.query.time,
            stat: { $ne: 'inprogress' },
          },
        });
      }
    },
    group() {
      this.computeResult();
    },
    gVal() {
      const g = this.gVal === 'unitId' ? undefined : this.gVal;
      this.$router.push({ query: { ...this.$route.query, g } });
    },
  },
};
</script>
